import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    output,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { UserStore } from "../../../stores/user.store";
import { SidebarStore } from "../../s25-sidebar/s25.sidebar.store";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { FormsModule } from "@angular/forms";
import { CreateServiceHourRequest, ServiceHoursService, ServiceHoursStatus } from "./service.hours.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Field, form, FormField, max, maxLength, min, required, submit, validate } from "@angular/forms/signals";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { EventMicroService } from "../../../services/event.micro.service";
import { Nullable } from "../../../pojo/Util";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { OrganizationMicroService } from "../../../services/organization.micro.service";
import { Category } from "../../../pojo/Category";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25Const } from "../../../util/s25-const";
import { Bind } from "../../../decorators/bind.decorator";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import { SeSecurityStore } from "../se.security.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { SeOrgStatusService } from "../se.org.status.service";
import { RoleRights } from "../securityConfig/role.rights";
import { AccessLevels } from "../../../pojo/Fls";
import { ServiceHourStatusComponent } from "./service.hour.status.component";
import RoleRightsList = RoleRights.RoleRightsList;
import { Event as EventPojo } from "../../../pojo/Event";
import EventStateIds = EventPojo.State.Ids;

type LoggedHours = {
    name: string;
    status: ServiceHoursStatus;
    contact: Nullable<DropDownItem>;
    event: Nullable<DropDownItem>;
    org: Nullable<DropDownItem>;
    date: Nullable<Date>;
    hours: Nullable<number>;
    description: Nullable<string>;
    denialReason: Nullable<string>;
};

@TypeManagerDecorator("service-hours-form")
@Component({
    selector: "service-hours-form",
    template: `
        <form>
            @if (canLogForUsers()) {
                <div class="checkbox-group">
                    <s25-ng-checkbox [value]="loggedForUser()" (valueChange)="loggedForUser.set($event)"
                        >I am logging hours for someone else</s25-ng-checkbox
                    >
                </div>
            }

            <div class="form-group">
                <label for="name" class="form-label"> Activity <span class="required">*</span> </label>
                <input id="name" class="form-input" [formField]="serviceHoursForm.name" />
                @let nameField = serviceHoursForm.name();
                @if (nameField.invalid() && nameField.touched()) {
                    @for (error of nameField.errors(); track error.kind) {
                        <p class="ngRed">
                            {{ error.message }}
                        </p>
                    }
                }
            </div>

            @if (loggedForUser()) {
                <div id="userSelectionGroup" class="form-group">
                    <label for="userId" class="form-label"> Select User <span class="required">*</span> </label>
                    <s25-contact-dropdown
                        [formField]="serviceHoursForm.contact"
                        [id]="'userId'"
                        [ariaLabel]="'Select User'"
                    ></s25-contact-dropdown>

                    @let contactField = serviceHoursForm.contact!();
                    @if (contactField.invalid() && contactField.touched()) {
                        <p class="ngRed">{{ contactField.errors()[0].message }}</p>
                    }
                </div>

                <div id="statusGroup" class="form-group">
                    <label class="form-label">Status</label>
                    <service-hour-status [(status)]="formStatus"></service-hour-status>
                </div>
            }

            <div class="form-group">
                <label for="orgs" class="form-label"> Organization <span class="required">*</span> </label>
                @if (orgs.resource.isLoading() || hostOrg.resource.isLoading()) {
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                } @else {
                    <div class="object-dropdown">
                        <s25-generic-dropdown
                            [items]="orgOptions()"
                            [searchEnabled]="true"
                            [id]="'orgs'"
                            [formField]="serviceHoursForm.org"
                        ></s25-generic-dropdown>
                        @if (selectedOrg()) {
                            <button class="aw-button aw-button--outline" (click)="resetSelected('org')">Clear</button>
                        }
                    </div>
                    @let orgField = serviceHoursForm.org!();
                    @if (orgField.invalid() && orgField.touched()) {
                        <p class="ngRed">{{ orgField.errors()[0].message }}</p>
                    }
                }
            </div>

            <div class="form-group">
                <label for="event" class="form-label"> Event</label>
                @if (events.resource.isLoading() || eventsForOrg.resource.isLoading()) {
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                } @else {
                    <div class="object-dropdown">
                        <s25-generic-dropdown
                            [items]="eventOptions()"
                            [searchEnabled]="true"
                            [id]="'event'"
                            [formField]="serviceHoursForm.event"
                        ></s25-generic-dropdown>
                        @if (selectedEvent()) {
                            <button class="aw-button aw-button--outline" (click)="resetSelected('event')">Clear</button>
                        }
                    </div>
                }
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label for="serviceDate" class="form-label">
                        Date of Service <span class="required">*</span>
                    </label>
                    <s25-datepicker [formField]="serviceHoursForm.date"></s25-datepicker>

                    @let dateField = serviceHoursForm.date!();
                    @if (dateField.invalid() && dateField.touched()) {
                        @for (error of dateField.errors(); track error.kind) {
                            <p class="ngRed">{{ error.message }}</p>
                        }
                    }
                </div>

                <div class="form-group">
                    <label for="hours" class="form-label"> Hours <span class="required">*</span> </label>
                    <input
                        type="number"
                        id="hours"
                        class="form-input"
                        step="0.5"
                        placeholder="0.0"
                        [formField]="serviceHoursForm.hours"
                    />
                    <p class="form-help">Enter hours in increments of 0.5</p>

                    @let hoursField = serviceHoursForm.hours!();
                    @if (hoursField.invalid() && hoursField.touched()) {
                        @for (error of hoursField.errors(); track error.kind) {
                            <p class="ngRed">{{ error.message }}</p>
                        }
                    }
                </div>
            </div>

            <div class="form-group">
                <label for="description" class="form-label"> Description </label>
                <textarea
                    id="description"
                    class="form-textarea"
                    placeholder="Provide details about the service activities completed..."
                    [formField]="serviceHoursForm.description"
                ></textarea>
            </div>

            @if (loggedForUser() && formStatus() === statusTypes.Denied) {
                <div id="denialReasonGroup" class="form-group">
                    <label for="denialReason" class="form-label"> Reason for Denial </label>
                    <textarea
                        id="denialReason"
                        class="form-textarea"
                        placeholder="Explain why these hours are being denied..."
                        [formField]="serviceHoursForm.denialReason"
                    ></textarea>
                </div>
            }

            @if (errorMsg()) {
                <p class="ngRed">{{ errorMsg() }}</p>
            }

            <s25-ng-fading-success-msg [(message)]="successMsg"></s25-ng-fading-success-msg>

            <div class="form-actions">
                <button class="aw-button aw-button--outline" (click)="sidebarStore.toggle()">Cancel</button>
                <s25-ng-button [type]="'primary'" [onClick]="onSubmit" [disabled]="!canSubmitHours()"
                    >Submit</s25-ng-button
                >
            </div>
        </form>
    `,
    styles: `
        .form-group {
            margin-bottom: 1rem;
        }

        .form-label {
            display: block;
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--color--text);
            margin-bottom: 0.5rem;
        }

        .required {
            color: var(--color--danger);
        }

        .form-input,
        .form-textarea,
        ::ng-deep .ngDropdownPaginated,
        ::ng-deep s25-datepicker .c-input {
            width: 100%;
            border: 1px solid var(--color--border) !important;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s ease;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .form-input:focus,
        .form-textarea:focus {
            outline: none;
            border-color: var(--color--border-bold);
        }

        #hours,
        .form-input,
        .form-textarea,
        ::ng-deep s25-datepicker .c-input {
            padding: 0.5rem 0.75rem;
        }

        ::ng-deep .ngDropdownPaginated .select2-arrow.ui-select-toggle {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
        }

        ::ng-deep .ngDropdownPaginated .select2-choice.ui-select-match {
            border-radius: 6px;
        }

        ::ng-deep .ngDropdownPaginated .ui-select-container.select2.select2-container {
            font-size: 0.875rem;
        }

        ::ng-deep s25-datepicker .c-input {
            height: unset;
        }

        .form-textarea {
            min-height: 100px;
            resize: vertical;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
        }

        .checkbox-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 1rem;
            background: var(--color--surface-2-bg);
            border-radius: 6px;
            margin-bottom: 1rem;
        }

        .form-help {
            font-size: 0.75rem;
            color: var(--color--text-muted);
            margin-top: 0.25rem;
        }

        .form-actions {
            display: flex;
            gap: 0.75rem;
            justify-content: flex-end;
            margin-top: 2rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .object-dropdown {
            display: flex;
            gap: 0.75rem;
        }

        .object-dropdown s25-generic-dropdown {
            flex: 1;
        }

        .ngRed {
            font-size: 0.8rem !important;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25CheckboxModule,
        S25ButtonComponent,
        FormsModule,
        FormField,
        S25DropdownPaginatedModule,
        S25DatepickerModule,
        S25LoadingInlineStaticComponent,
        S25FadingSuccessMsgComponent,
        ServiceHourStatusComponent,
    ],
})
export class ServiceHoursFormComponent {
    readonly refresh = output();

    protected loggedForUser = signal(false);
    protected errorMsg = signal("");
    protected successMsg = signal("");

    protected serviceHoursModel = signal<LoggedHours>({
        name: "",
        status: ServiceHoursStatus.Pending,
        contact: null,
        event: null,
        org: null,
        date: new Date(),
        hours: null,
        description: "",
        denialReason: null,
    });
    serviceHoursForm = form(this.serviceHoursModel, (path) => {
        required(path.name, { message: "An Activity Name is required" });
        maxLength(path.name, 255, { message: "Activity Name cannot exceed 255 characters" });
        required(path.org!, { message: "An Organization is required" });
        required(path.hours!, { message: "Service Hours are required" });
        min(path.hours!, 0.5, { message: "Service Hours are required" });
        max(path.hours!, 99_999_999.5, {
            message: "Service Hours entered exceed the maximum allowed of 99,999,999.5 ",
        });
        validate(path.date!, ({ value }) => {
            const isValidDate = S25Util.date.isValid(value());
            if (!isValidDate) return { kind: "date", message: "Please enter a valid date" };
            return null;
        });
        validate(path.date!, ({ value }) => {
            const isFuture = S25Util.date.getDate(value()) > S25Util.date.currentDate();
            if (isFuture) return { kind: "future", message: "Service Hours cannot be logged for a future date" };
            return null;
        });
        validate(path.contact!, ({ value }) => {
            if (!this.loggedForUser()) return null;
            if (!value()) return { kind: "required", message: "A contact is required" };
            return null;
        });
    });

    private userStore = inject(UserStore);
    protected sidebarStore = inject(SidebarStore);
    private securityStore = inject(SeSecurityStore);
    private orgSecStore = inject(SeOrgSecurityStore, { optional: true });
    private serviceHoursService = inject(ServiceHoursService);

    protected statusTypes = ServiceHoursStatus;

    protected formStatus = linkedSignal(() => this.serviceHoursModel().status);

    constructor() {
        effect(() => {
            const status = this.formStatus();
            this.serviceHoursModel.update((m) => (m.status === status ? m : { ...m, status }));
        });

        effect(() => {
            const isLoggingForUser = this.loggedForUser();
            this.serviceHoursForm.contact!().value.set(isLoggingForUser ? null : this.signedInUser());
        });

        effect(() => {
            const hostOrg = this.hostOrg.value();
            if (!hostOrg) return;
            this.serviceHoursForm.org!().value.set(hostOrg);
        });
    }

    readonly signedInUser = computed<DropDownItem>(() => {
        const { id, username } = this.userStore;
        return { itemId: id(), itemName: username() };
    });

    readonly selectedOrgId = computed(() => this.selectedOrg()?.itemId);

    readonly selectedOrgStatus = s25Resource({
        params: () => {
            const orgId = this.selectedOrgId();
            if (!orgId || this.orgSecStore?.orgId() === orgId) {
                return undefined;
            }
            return orgId;
        },
        loader: async ({ params: orgId }) => SeOrgStatusService.resolveEffects(Number(orgId)),
    });

    readonly canSubmitHours = computed(() => {
        const orgId = this.selectedOrgId();
        if (!orgId) {
            return false;
        }
        if (this.orgSecStore?.orgId() === orgId) {
            return this.orgSecStore.canLogServiceHours();
        }
        const logEffect = this.selectedOrgStatus
            .value()
            ?.statusEffects?.find((effect) => effect.function === "LOG_SERVICE_HOURS");
        return !logEffect?.blocksAccess;
    });

    readonly canLogForUsers = this.securityStore.orgAccessSignal(
        this.selectedOrgId,
        RoleRightsList.SE_ORG_CONTACTS,
        AccessLevels.Full,
    );

    protected events = s25Resource({
        loader: async () => {
            const [events] = await S25Util.Maybe(
                EventMicroService.getEvents({
                    category_id: [Category.SystemDefined.StudentEngagement],
                    node_type: "E",
                    state: [EventStateIds.Confirmed, EventStateIds.Tentative, EventStateIds.Draft],
                }),
            );
            if (events) return events.content?.data?.items ?? [];
        },
    });

    protected orgs = s25Resource({
        loader: async () => {
            const [orgs] = await S25Util.Maybe(
                OrganizationMicroService.getOrganizations({
                    attribute_id: CustomAttributes.system.StudentEngagementOrg.id,
                    attribute_value: "true",
                }),
            );
            if (orgs) return orgs.content?.data?.items ?? [];
        },
    });

    readonly selectedEvent = computed(() => this.serviceHoursModel().event);
    readonly selectedOrg = computed(() => this.serviceHoursModel().org);

    readonly eventOptions = computed<DropDownItem[]>(() => {
        const selectedOrg = this.selectedOrg();
        const events = selectedOrg ? this.eventsForOrg.value() : this.events.value();
        return events?.map((event) => ({ itemId: event.id, itemName: event.eventName })) ?? [];
    });

    readonly orgOptions = computed(() => {
        const hostOrg = this.hostOrg.value();
        if (hostOrg) return [hostOrg];
        return this.orgs.value()?.map((org) => ({ itemId: org.id, itemName: org.organizationName })) ?? [];
    });

    protected hostOrg = s25Resource({
        if: () => !!this.selectedEvent()?.itemId,
        params: () => this.selectedEvent(),
        loader: async ({ params: selectedEvent }) => {
            // If event is in the current org's event list, host org hasn't changed
            const currentOrg = this.selectedOrg();
            if (currentOrg && this.eventsForOrg.value()?.some((event) => event.id === selectedEvent!.itemId)) {
                return currentOrg;
            }

            const [event] = await S25Util.Maybe(
                EventMicroService.getEventDetailById([Number(selectedEvent!.itemId)], true, ["organizations"]),
            );
            if (!event) return undefined;
            const eventData = event.content;
            const hostOrgId = eventData?.data?.items?.[0]?.organizations?.primary;
            if (hostOrgId) {
                const hostOrgName =
                    eventData?.expandedInfo?.organizations?.find((org) => org.organizationId === hostOrgId)
                        ?.organizationName ?? S25Const.private;
                return { itemId: hostOrgId, itemName: hostOrgName };
            }
        },
    });

    protected eventsForOrg = s25Resource({
        if: () => !!this.selectedOrg()?.itemId,
        params: () => this.selectedOrg(),
        loader: async ({ params: selectedOrg }) => {
            const [events] = await S25Util.Maybe(
                EventMicroService.getEvents({
                    primary_organization_id: Number(selectedOrg!.itemId),
                    category_id: [Category.SystemDefined.StudentEngagement],
                    node_type: "E",
                }),
            );
            return events.content?.data?.items;
        },
    });

    resetSelected(objType: "event" | "org") {
        this.serviceHoursModel.update((model) => ({ ...model, [objType]: null }));
    }

    @Bind
    onSubmit() {
        return submit(this.serviceHoursForm, async () => {
            if (this.serviceHoursForm().invalid()) return;
            if (!this.canSubmitHours()) {
                this.errorMsg.set("This organization does not allow logging service hours.");
                return;
            }

            const payload = this.setupPayload();
            const [_, error] = await S25Util.Maybe(this.serviceHoursService.createServiceHour(payload));
            if (error) {
                this.errorMsg.set(<string>S25Util.errorText(error));
                return;
            }
            this.errorMsg.set("");
            this.successMsg.set("Hours logged successfully");
            this.resetForm();
            this.refresh.emit();
        });
    }

    setupPayload() {
        const { org, date, name, hours, description, event, status, denialReason, contact } = this.serviceHoursModel();
        const payload: CreateServiceHourRequest = {
            organizationId: Number(org!.itemId),
            serviceDate: date!,
            activityName: name,
            hours: hours!,
            description: description!,
            status: status,
            ...(!!event?.itemId && { eventId: Number(event.itemId) }),
        };
        if (this.loggedForUser()) {
            payload.contId = Number(contact!.itemId);
            if (status !== ServiceHoursStatus.Pending) payload.verifiedByContId = Number(this.signedInUser().itemId);
            if (status === ServiceHoursStatus.Denied) payload.denialReason = denialReason!;
        }

        return payload;
    }

    resetForm() {
        this.serviceHoursForm().reset();
        this.serviceHoursModel.set({
            name: "",
            status: ServiceHoursStatus.Pending,
            contact: null,
            event: null,
            org: null,
            date: new Date(),
            hours: null,
            description: "",
            denialReason: null,
        });
    }
}
